import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { SkuAnnotationTool } from './sku-annotation-toolbar';
import type { AnnotationPreset } from './types';
import type { StyleRun } from '@rta/rta-blueprint-library';
import type { AnnotationPreviewPatch, StyleRunPatch } from './helpers';

export type AnnotationContextMenu = {
	x: number;
	y: number;
	annotationId: string;
} | null;

export type ActiveTextSelection = { start: number; end: number; text: string } | null;

/** The last non-empty textarea range and the annotation that owns it -- what a format is applied to */
export type AnnotationFormatSelection = { annotationId: string; start: number; end: number } | null;

/** An uncommitted style edit for live feedback while a picker is dragged; `range` null means the whole box */
export type AnnotationStylePreview = {
	annotationId: string;
	patch: AnnotationPreviewPatch;
	range: { start: number; end: number } | null;
} | null;

/** The runs an editor holds locally for the annotation it is editing, before they are committed to the model */
export type AnnotationLiveStyleRuns = { annotationId: string; runs: Array<StyleRun> } | null;

const RECENT_COLOR_LIMIT = 8;

/** A value held outside React state with its own subscribers, so writing it re-renders only the components that read it */
export type AnnotationStore<T> = {
	get: () => T;
	set: (value: T) => void;
	subscribe: (listener: () => void) => () => void;
};

const createAnnotationStore = <T,>(initial: T): AnnotationStore<T> => {
	let current = initial;
	const listeners = new Set<() => void>();

	return {
		get: (): T => current,
		set: (value: T): void => { current = value; listeners.forEach((listener) => listener()); },
		subscribe: (listener: () => void): (() => void) => {
			listeners.add(listener);
			return (): void => { listeners.delete(listener); };
		}
	};
};

export type SkuAnnotationContextType = {
	activeTool: SkuAnnotationTool;
	setActiveTool: (tool: SkuAnnotationTool) => void;
	selectedAnnotationId: string | null;
	setSelectedAnnotationId: (id: string | null) => void;
	selectedAnnotationIds: Array<string>;
	setSelectedAnnotationIds: (ids: Array<string>) => void;
	toggleAnnotationSelection: (id: string, additive: boolean) => void;
	editingAnnotationId: string | null;
	setEditingAnnotationId: (id: string | null) => void;
	activeTextSelection: ActiveTextSelection;
	setActiveTextSelection: (selection: ActiveTextSelection) => void;
	// INVARIANT: formatSelection holds the last NON-EMPTY range for its annotation and is cleared only when that annotation's edit session genuinely ends -- never on a collapsed select event and never on a blur into the toolbar.
	formatSelection: AnnotationFormatSelection;
	setFormatSelection: (selection: AnnotationFormatSelection) => void;
	// Set true on toolbar pointerdown so an editor blurring into the toolbar does not end the edit session
	toolbarInteractingRef: React.MutableRefObject<boolean>;
	// Registered by whichever editor is live (it owns the textarea, the range and its own collection) and invoked by the toolbar, so the toolbar never guesses which surface owns the selection
	applyStyleRef: React.MutableRefObject<((patch: StyleRunPatch) => void) | null>;
	// The live editor's uncommitted runs, so the toolbar's lit states read what the user sees instead of the model it has not been written to yet
	liveStyleRunsRef: React.MutableRefObject<AnnotationLiveStyleRuns>;
	// Ref-backed stores, read through useAnnotationStylePreview/useRecentAnnotationColors: a picker drag must not publish a new context value (and re-render every overhead/blueprint-page consumer) once per frame
	stylePreviewStore: AnnotationStore<AnnotationStylePreview>;
	setStylePreview: (preview: AnnotationStylePreview) => void;
	recentColorsStore: AnnotationStore<Array<string>>;
	pushRecentColor: (color: string) => void;
	/** @deprecated superseded by remapStyleRuns; remove once both editors remap runs against their own previous text. */
	colorRunsSourceText: string | null;
	setColorRunsSourceText: (text: string | null) => void;
	contextMenu: AnnotationContextMenu;
	setContextMenu: (menu: AnnotationContextMenu) => void;
	pendingSaveRef: React.MutableRefObject<(() => void) | null>;
	// Injected from react-ui (#354) so "Save to presets" can reach the API without this package owning a fetch call; the menu item hides until it's wired
	onSaveAnnotationPreset?: (name: string, annotations: Array<Record<string, unknown>>) => void;
	// The user's saved presets and a delete callback, both supplied by react-ui (it owns the fetch/refresh)
	annotationPresets: Array<AnnotationPreset>;
	onDeleteAnnotationPreset?: (presetId: string) => void;
};

const defaultPendingSaveRef = { current: null };
const defaultToolbarInteractingRef = { current: false };
const defaultApplyStyleRef = { current: null };
const defaultLiveStyleRunsRef = { current: null };
const defaultStylePreviewStore = createAnnotationStore<AnnotationStylePreview>(null);
const defaultRecentColorsStore = createAnnotationStore<Array<string>>([]);

export const SkuAnnotationContext = createContext<SkuAnnotationContextType>({
	activeTool: 'select',
	setActiveTool: () => {},
	selectedAnnotationId: null,
	setSelectedAnnotationId: () => {},
	selectedAnnotationIds: [],
	setSelectedAnnotationIds: () => {},
	toggleAnnotationSelection: () => {},
	editingAnnotationId: null,
	setEditingAnnotationId: () => {},
	activeTextSelection: null,
	setActiveTextSelection: () => {},
	formatSelection: null,
	setFormatSelection: () => {},
	toolbarInteractingRef: defaultToolbarInteractingRef,
	applyStyleRef: defaultApplyStyleRef,
	liveStyleRunsRef: defaultLiveStyleRunsRef,
	stylePreviewStore: defaultStylePreviewStore,
	setStylePreview: () => {},
	recentColorsStore: defaultRecentColorsStore,
	pushRecentColor: () => {},
	colorRunsSourceText: null,
	setColorRunsSourceText: () => {},
	contextMenu: null,
	setContextMenu: () => {},
	pendingSaveRef: defaultPendingSaveRef,
	annotationPresets: []
});

export const useSkuAnnotation = (): SkuAnnotationContextType => useContext(SkuAnnotationContext);

/** The uncommitted preview patch for ONE annotation, so a picker drag re-renders that annotation instead of every context consumer */
export const useAnnotationStylePreview = (annotationId: string | null): AnnotationStylePreview => {
	const { stylePreviewStore } = useSkuAnnotation();
	const [preview, setPreview] = useState<AnnotationStylePreview>(null);

	useEffect((): (() => void) => {
		const sync = (): void => {
			const next = stylePreviewStore.get();
			setPreview(next && next.annotationId === annotationId ? next : null);
		};
		sync();
		return stylePreviewStore.subscribe(sync);
	}, [annotationId, stylePreviewStore]);

	return preview;
};

/** The recent-colour list for the toolbar's pickers; subscribed here so committing a colour never re-renders the render tree */
export const useRecentAnnotationColors = (): Array<string> => {
	const { recentColorsStore } = useSkuAnnotation();
	const [recentColors, setRecentColors] = useState<Array<string>>(() => recentColorsStore.get());

	useEffect((): (() => void) => {
		const sync = (): void => setRecentColors(recentColorsStore.get());
		sync();
		return recentColorsStore.subscribe(sync);
	}, [recentColorsStore]);

	return recentColors;
};

export type SkuAnnotationProviderProps = {
	children: React.ReactNode;
	onSaveAnnotationPreset?: (name: string, annotations: Array<Record<string, unknown>>) => void;
	annotationPresets?: Array<AnnotationPreset>;
	onDeleteAnnotationPreset?: (presetId: string) => void;
};

export const SkuAnnotationProvider = (props: SkuAnnotationProviderProps): React.ReactElement => {
	const { children, onSaveAnnotationPreset, annotationPresets = [], onDeleteAnnotationPreset } = props;
	const [activeTool, setActiveTool] = useState<SkuAnnotationTool>('select');
	// selectedAnnotationId is derived: the "primary" (most-recently-added) member of the multi-selection, or null when empty
	const [selectedAnnotationIds, setSelectedAnnotationIds] = useState<Array<string>>([]);
	const selectedAnnotationId = selectedAnnotationIds.length > 0 ? selectedAnnotationIds[selectedAnnotationIds.length - 1] : null;
	const [editingAnnotationId, setEditingAnnotationId] = useState<string | null>(null);
	const [activeTextSelection, setActiveTextSelection] = useState<ActiveTextSelection>(null);
	const [formatSelection, setFormatSelection] = useState<AnnotationFormatSelection>(null);
	const stylePreviewStore = useMemo((): AnnotationStore<AnnotationStylePreview> => createAnnotationStore<AnnotationStylePreview>(null), []);
	const recentColorsStore = useMemo((): AnnotationStore<Array<string>> => createAnnotationStore<Array<string>>([]), []);
	const [colorRunsSourceText, setColorRunsSourceText] = useState<string | null>(null);
	const [contextMenu, setContextMenu] = useState<AnnotationContextMenu>(null);
	const pendingSaveRef = useRef<(() => void) | null>(null);
	const toolbarInteractingRef = useRef<boolean>(false);
	const applyStyleRef = useRef<((patch: StyleRunPatch) => void) | null>(null);
	const liveStyleRunsRef = useRef<AnnotationLiveStyleRuns>(null);

	const handleSetSelectedAnnotationId = useCallback((id: string | null): void => {
		setSelectedAnnotationIds(id ? [id] : []);
		setFormatSelection((previous) => (previous && previous.annotationId === id ? previous : null));
	}, []);

	// Shift/Ctrl/Cmd-click: add or remove a single id from the current selection without disturbing the rest
	const toggleAnnotationSelection = useCallback((id: string, additive: boolean): void => {
		if (!additive) { handleSetSelectedAnnotationId(id); return; }
		setSelectedAnnotationIds((prev) => (prev.includes(id) ? prev.filter((existingId) => existingId !== id) : [...prev, id]));
	}, [handleSetSelectedAnnotationId]);

	const pushRecentColor = useCallback((color: string): void => {
		if (!color) return;
		const { get, set } = recentColorsStore;
		set([color, ...get().filter((existing) => existing !== color)].slice(0, RECENT_COLOR_LIMIT));
	}, [recentColorsStore]);

	const value = useMemo((): SkuAnnotationContextType => ({
		activeTool, setActiveTool,
		selectedAnnotationId, setSelectedAnnotationId: handleSetSelectedAnnotationId,
		selectedAnnotationIds, setSelectedAnnotationIds, toggleAnnotationSelection,
		editingAnnotationId, setEditingAnnotationId,
		activeTextSelection, setActiveTextSelection,
		formatSelection, setFormatSelection,
		toolbarInteractingRef, applyStyleRef, liveStyleRunsRef,
		stylePreviewStore, setStylePreview: stylePreviewStore.set,
		recentColorsStore, pushRecentColor,
		colorRunsSourceText, setColorRunsSourceText,
		contextMenu, setContextMenu,
		pendingSaveRef,
		onSaveAnnotationPreset,
		annotationPresets, onDeleteAnnotationPreset
	}), [activeTool, selectedAnnotationId, selectedAnnotationIds, editingAnnotationId, activeTextSelection, formatSelection, stylePreviewStore, recentColorsStore, pushRecentColor, colorRunsSourceText, contextMenu, handleSetSelectedAnnotationId, toggleAnnotationSelection, onSaveAnnotationPreset, annotationPresets, onDeleteAnnotationPreset]);

	return (
		<SkuAnnotationContext.Provider value={value}>
			{children}
		</SkuAnnotationContext.Provider>
	);
};
